import { useState } from 'react';
import {
    Button,
    Dialog,
    Flex,
    pxToRem,
    useAppDisplayModeContext,
} from '@archipro-design/aria';
import { Caption, useStyles, Checkbox, useTheme } from '@archipro-design/aria';
import * as S from './DownloadConfirmationModal.style';
import BottomDrawer from '~/modules/detail/components/bottom-drawer/BottomDrawer';
import { CloseLine } from '@archipro-design/icons';

interface DownloadConfirmationModalProps {
    modalOpen: boolean;
    toggleModal: () => void;
    onDownload: () => void;
    dontAskAgainChecked: boolean;
    updateDontAskAgain: (value: boolean) => void;
}

const DownloadConfirmationModal = ({
    modalOpen,
    toggleModal,
    onDownload,
    dontAskAgainChecked,
    updateDontAskAgain,
}: DownloadConfirmationModalProps) => {
    const { css } = useStyles();
    const theme = useTheme();

    const [dontAskAgainCheckBox, setDontAskAgainCheckBox] =
        useState(dontAskAgainChecked);

    const { mobile } = useAppDisplayModeContext();

    return (
        <>
            {mobile ? (
                <BottomDrawer
                    open={modalOpen}
                    toggleModal={toggleModal}
                    title="Download"
                    contentHeight={355}
                >
                    <Flex
                        column
                        vAlign="center"
                        hAlign="center"
                        data-testid="download-confirmation-modal"
                    >
                        <Caption
                            className={css(S.DownloadMessage)}
                            variables={{
                                lineHeight: theme.siteVariables.lineHeightSmall,
                            }}
                        >
                            By choosing to download this file, you acknowledge
                            that your name and email will be shared with the
                            seller
                        </Caption>
                        <Flex vAlign="center">
                            <Checkbox
                                checked={dontAskAgainCheckBox}
                                onChange={(_, data) => {
                                    setDontAskAgainCheckBox(!!data?.checked);
                                    updateDontAskAgain(!!data?.checked);
                                }}
                            />
                            <Caption
                                className={css(S.DontAskAgain)}
                                variant="03"
                                variables={{
                                    letterSpacing:
                                        theme.siteVariables.letterSpacingMedium,
                                    lineHeight:
                                        theme.siteVariables.lineHeightSmallest,
                                }}
                            >
                                {`Don't ask me again`}
                            </Caption>
                        </Flex>
                        <Button
                            size={16}
                            className={css(S.DownloadButton)}
                            variant="contained"
                            onClick={onDownload}
                        >
                            DOWNLOAD
                        </Button>
                    </Flex>
                </BottomDrawer>
            ) : (
                <Dialog
                    open={modalOpen}
                    onCancel={() => {
                        toggleModal();
                    }}
                    content={{
                        role: 'dialog',
                        'aria-label': 'Download confirmation',
                        content: (
                            <>
                                <Flex
                                    hAlign="end"
                                    data-testid="download-confirmation-modal"
                                >
                                    <CloseLine
                                        className={css(S.CloseDialog)}
                                        onClick={() => toggleModal()}
                                    />
                                </Flex>
                                <Caption
                                    className={css(S.DownloadMessage)}
                                    variables={{
                                        lineHeight:
                                            theme.siteVariables.lineHeightSmall,
                                        fontSize: pxToRem(18),
                                    }}
                                >
                                    By choosing to download this file, you
                                    acknowledge that your name and email will be
                                    shared with the seller
                                </Caption>
                                <Flex vAlign="center" space="between">
                                    <Flex vAlign="center">
                                        <Checkbox
                                            checked={dontAskAgainCheckBox}
                                            onChange={(_, data) => {
                                                setDontAskAgainCheckBox(
                                                    !!data?.checked
                                                );
                                                updateDontAskAgain(
                                                    !!data?.checked
                                                );
                                            }}
                                        />
                                        <Caption
                                            className={css(S.DontAskAgain)}
                                            variant="03"
                                            variables={{
                                                letterSpacing:
                                                    theme.siteVariables
                                                        .letterSpacingMedium,
                                                lineHeight:
                                                    theme.siteVariables
                                                        .lineHeightSmallest,
                                            }}
                                        >
                                            {`Don't ask me again`}
                                        </Caption>
                                    </Flex>
                                    <Button
                                        size={16}
                                        className={css(S.DownloadButton)}
                                        variant="contained"
                                        onClick={() => {
                                            onDownload();
                                        }}
                                    >
                                        DOWNLOAD
                                    </Button>
                                </Flex>
                            </>
                        ),
                    }}
                    footer={null}
                    variables={{
                        rootBorderRadius: 0,
                        rootWidth: pxToRem(898),
                        contentMargin: 0,
                    }}
                />
            )}
        </>
    );
};

export default DownloadConfirmationModal;
